<script setup lang="ts">
import { z } from 'zod'
import { zodResolver } from '@primeuix/forms/resolvers/zod'
import { useToast } from '@/shared/composables/UseToast'
import { useDynamicDialogLifecycle } from '@/shared/composables/UseDynamicDialogLifecycle'
import SubscriptionInputSelect from '@/shared/components/form/SubscriptionInputSelect.vue'
import SalesDemoSourceFields from '@/features/sales/components/SalesDemoSourceFields.vue'
import {
  useAdminGetSalesDailyLog,
  useAdminUpdateSalesDailyLog,
  getAdminListSalesDailyLogsQueryKey,
  getAdminListMySalesDailyLogsQueryKey,
  getAdminGetSalesStatsQueryKey,
  getAdminGetMySalesStatsQueryKey,
  getAdminListSalesDemosQueryKey,
} from '@swipe4work/api-orval'
import type { SalesDemoPotentialSubscriptionType, SalesDemoSource } from '@swipe4work/models-orval'
import { AdminPermissionResource, PermissionLevel } from '@swipe4work/models-orval'
import { useQueryClient } from '@tanstack/vue-query'

const toast = useToast()
const { closeDialog, dialogData } = useDynamicDialogLifecycle<{ logId: number }>()
const queryClient = useQueryClient()

const logId = computed(() => dialogData.value.logId!)

const { data: logData, isPending: isLoadingLog } = useAdminGetSalesDailyLog(logId)
const { mutateAsync: updateLog, isPending: isSaving } = useAdminUpdateSalesDailyLog()

type DemoSubscriptionType = SalesDemoPotentialSubscriptionType | 'None'

interface DemoForm {
  id: number | null
  companyName: string
  contactFirstName: string
  contactLastName: string
  contactEmail: string
  demoDate: Date | null
  potentialCloseDate: Date | null
  source: SalesDemoSource | null
  sourceOther: string
  subscriptionType: DemoSubscriptionType
  matchCredits: number | null
  subscriptionId: number | null
  onboardingInviteSent: boolean
}

function parseDate(raw: unknown): Date | null {
  if (raw == null || raw === '') return null
  const d = raw instanceof Date ? raw : new Date(raw as string)

  return Number.isNaN(d.getTime()) ? null : d
}

const date = ref<Date>(new Date())
const plannedDemos = ref<number | null>(null)
const demoForms = ref<DemoForm[]>([])

watch(logData, (log) => {
  if (!log) return
  date.value = parseDate(log.date) ?? new Date()
  plannedDemos.value = log.plannedDemos
  demoForms.value = log.demos.map(d => ({
    id: d.id,
    companyName: d.companyName,
    contactFirstName: d.contactFirstName ?? '',
    contactLastName: d.contactLastName ?? '',
    contactEmail: d.contactEmail ?? '',
    demoDate: parseDate(d.demoDate),
    potentialCloseDate: parseDate(d.potentialCloseDate),
    source: d.source ?? null,
    sourceOther: d.sourceOther ?? '',
    subscriptionType: (d.potentialSubscriptionType ?? 'None') as DemoSubscriptionType,
    matchCredits: d.potentialMatchCredits ?? null,
    subscriptionId: d.potentialSubscriptionId ?? null,
    onboardingInviteSent: d.onboardingInviteSent,
  }))
}, { immediate: true })

watch(plannedDemos, (count) => {
  const n = count ?? 0
  if (n > demoForms.value.length) {
    for (let i = demoForms.value.length; i < n; i++) {
      demoForms.value.push({
        id: null,
        companyName: '',
        contactFirstName: '',
        contactLastName: '',
        contactEmail: '',
        demoDate: null,
        potentialCloseDate: null,
        source: null,
        sourceOther: '',
        subscriptionType: 'None',
        matchCredits: null,
        subscriptionId: null,
        onboardingInviteSent: false,
      })
    }
  }
  else {
    demoForms.value = demoForms.value.slice(0, n)
  }
})

const baseSchema = z.object({
  calledCompanies: z.number({ required_error: 'Gebeld is verplicht' }).min(0, 'Minimaal 0'),
  conversations: z.number({ required_error: 'Gesprekken is verplicht' }).min(0, 'Minimaal 0'),
  plannedDemos: z.number({ required_error: 'Geplande demo\'s is verplicht' }).min(0, 'Minimaal 0'),
})

const resolver = zodResolver(baseSchema)

const demoErrors = ref<Record<number, Record<string, string>>>({})
const hasTriedSubmit = ref(false)

function validateDemos(): boolean {
  demoErrors.value = {}
  let valid = true

  for (let i = 0; i < demoForms.value.length; i++) {
    const demo = demoForms.value[i]
    const errors: Record<string, string> = {}
    if (!demo.companyName.trim()) {
      errors.companyName = 'Bedrijfsnaam is verplicht'
      valid = false
    }
    if (demo.contactEmail.trim() && !/^[^\s@]+@[^\s@][^\s.@]*\.[^\s@]+$/.test(demo.contactEmail)) {
      errors.contactEmail = 'Ongeldig e-mailadres'
      valid = false
    }
    if (!demo.source) {
      errors.source = 'Bron is verplicht'
      valid = false
    }
    if (demo.source === 'Other' && !demo.sourceOther.trim()) {
      errors.sourceOther = 'Vul in via welk kanaal de demo binnenkwam'
      valid = false
    }
    if (Object.keys(errors).length > 0) {
      demoErrors.value[i] = errors
    }
  }

  return valid
}

function toDateOnly(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

async function onSubmit({ values, valid }: {
  values: any,
  valid: boolean 
}) {
  hasTriedSubmit.value = true

  if (!valid || !values) return

  const demosValid = validateDemos()
  if (!demosValid) return

  try {
    await updateLog({
      id: logId.value,
      data: {
        date: toDateOnly(date.value) as unknown as Date,
        calledCompanies: values.calledCompanies,
        conversations: values.conversations,
        plannedDemos: values.plannedDemos,
        demos: demoForms.value.map(d => ({
          id: d.id,
          companyName: d.companyName,
          contactFirstName: d.contactFirstName.trim() || null,
          contactLastName: d.contactLastName.trim() || null,
          contactEmail: d.contactEmail.trim() || null,
          demoDate: d.demoDate ?? null,
          potentialCloseDate: d.potentialCloseDate ?? null,
          // validateDemos already rejected a demo without a source.
          source: d.source ?? undefined,
          sourceOther: d.source === 'Other' ? d.sourceOther.trim() : null,
          potentialSubscriptionType: d.subscriptionType === 'None' ? undefined : d.subscriptionType,
          potentialSubscriptionId: d.subscriptionType === 'VacancySlots' ? d.subscriptionId : null,
          potentialMatchCredits: d.subscriptionType === 'MatchCredits' ? d.matchCredits : null,
        })),
      },
    })

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getAdminListSalesDailyLogsQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getAdminListMySalesDailyLogsQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getAdminGetSalesStatsQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getAdminGetMySalesStatsQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getAdminListSalesDemosQueryKey() }),
    ])

    toast.success('Log bijgewerkt', 'Het sales log is succesvol bijgewerkt')
    closeDialog()
  }
  catch {
    toast.error('Fout', 'Er is een fout opgetreden bij het bijwerken van het log')
  }
}

const subscriptionTypeOptions = [
  {
    label: 'Geen',
    value: 'None', 
  },
  {
    label: 'Match Credits',
    value: 'MatchCredits', 
  },
  {
    label: 'Vacature Slots',
    value: 'VacancySlots', 
  },
]
</script>

<template>
  <section class="max-h-screen min-w-lg flex flex-col gap-4 overflow-y-auto">
    <div class="mt-5 flex flex-col gap-1">
      <h2>
        Sales Log Bewerken
      </h2>
      <p class="text-sm text-surface-500">
        Pas je dagelijkse sales activiteiten aan.
      </p>
    </div>

    <div v-if="isLoadingLog || !logData" class="flex flex-col gap-3">
      <Skeleton v-for="n in 4" :key="n" height="3rem" />
    </div>

    <Form
      v-else
      :resolver="resolver"
      :initial-values="{
        calledCompanies: logData.calledCompanies,
        conversations: logData.conversations,
        plannedDemos: logData.plannedDemos,
      }"
      class="flex flex-col gap-4"
      @submit="onSubmit"
    >
      <div class="grid grid-cols-2 gap-3">
        <div class="col-span-2 flex flex-col gap-1">
          <label for="logDate">Datum</label>
          <DatePicker
            v-model="date"
            input-id="logDate"
            date-format="dd-mm-yy"
            show-button-bar
            class="w-full"
          />
        </div>

        <FormField v-slot="$field" name="calledCompanies" class="flex flex-col gap-1">
          <label for="calledCompanies">Gebeld</label>
          <InputNumber
            v-bind="$field.props"
            input-id="calledCompanies"
            :min="0"
            :invalid="$field?.invalid"
            class="w-full"
          />
          <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">
            {{ $field.error?.message }}
          </Message>
        </FormField>

        <FormField v-slot="$field" name="conversations" class="flex flex-col gap-1">
          <label for="conversations">Gesprekken</label>
          <InputNumber
            v-bind="$field.props"
            input-id="conversations"
            :min="0"
            :invalid="$field?.invalid"
            class="w-full"
          />
          <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">
            {{ $field.error?.message }}
          </Message>
        </FormField>

        <FormField v-slot="$field" name="plannedDemos" class="col-span-2 flex flex-col gap-1">
          <label for="plannedDemos">Geplande Demo's</label>
          <InputNumber
            v-bind="$field.props"
            input-id="plannedDemos"
            :min="0"
            :invalid="$field?.invalid"
            class="w-full"
            @update:model-value="(v: number | null) => { plannedDemos = v }"
          />
          <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">
            {{ $field.error?.message }}
          </Message>
        </FormField>
      </div>

      <template v-if="demoForms.length > 0">
        <Divider />
        <div class="flex flex-col gap-6">
          <div
            v-for="(demo, index) in demoForms"
            :key="index"
            class="flex flex-col gap-3 border border-surface-200 rounded-lg p-4"
          >
            <h4 class="text-sm font-semibold">
              Demo {{ index + 1 }}
            </h4>

            <Message v-if="demo.onboardingInviteSent" severity="info" size="small">
              De onboarding uitnodiging is al verstuurd. Contactgegevens en de onboarding datum zijn vastgezet.
            </Message>

            <div class="grid grid-cols-2 gap-3">
              <div class="col-span-2 flex flex-col gap-1">
                <label :for="`companyName-${index}`">Bedrijfsnaam</label>
                <InputText
                  :id="`companyName-${index}`"
                  v-model="demo.companyName"
                  class="w-full"
                  :invalid="hasTriedSubmit && !!demoErrors[index]?.companyName"
                />
                <Message v-if="hasTriedSubmit && demoErrors[index]?.companyName" severity="error" size="small" variant="simple">
                  {{ demoErrors[index]?.companyName }}
                </Message>
              </div>

              <div class="flex flex-col gap-1">
                <label :for="`firstName-${index}`">Voornaam (optioneel)</label>
                <InputText
                  :id="`firstName-${index}`"
                  v-model="demo.contactFirstName"
                  :disabled="demo.onboardingInviteSent"
                  class="w-full"
                />
              </div>

              <div class="flex flex-col gap-1">
                <label :for="`lastName-${index}`">Achternaam (optioneel)</label>
                <InputText
                  :id="`lastName-${index}`"
                  v-model="demo.contactLastName"
                  :disabled="demo.onboardingInviteSent"
                  class="w-full"
                />
              </div>

              <div class="col-span-2 flex flex-col gap-1">
                <label :for="`contactEmail-${index}`">E-mailadres (optioneel)</label>
                <InputText
                  :id="`contactEmail-${index}`"
                  v-model="demo.contactEmail"
                  :disabled="demo.onboardingInviteSent"
                  class="w-full"
                  :invalid="hasTriedSubmit && !!demoErrors[index]?.contactEmail"
                />
                <Message v-if="hasTriedSubmit && demoErrors[index]?.contactEmail" severity="error" size="small" variant="simple">
                  {{ demoErrors[index]?.contactEmail }}
                </Message>
              </div>

              <div class="flex flex-col gap-1">
                <label :for="`demoDate-${index}`">Demo datum (optioneel)</label>
                <DatePicker
                  v-model="demo.demoDate"
                  :input-id="`demoDate-${index}`"
                  date-format="dd-mm-yy"
                  show-button-bar
                  show-clear
                  class="w-full"
                />
              </div>

              <div class="flex flex-col gap-1">
                <label :for="`closeDate-${index}`">Potentiële onboarding datum (optioneel)</label>
                <DatePicker
                  v-model="demo.potentialCloseDate"
                  :input-id="`closeDate-${index}`"
                  date-format="dd-mm-yy"
                  show-button-bar
                  show-clear
                  :disabled="demo.onboardingInviteSent"
                  class="w-full"
                />
              </div>

              <SalesDemoSourceFields
                v-model:source="demo.source"
                v-model:source-other="demo.sourceOther"
                :id-suffix="String(index)"
                :source-error="hasTriedSubmit ? demoErrors[index]?.source : undefined"
                :source-other-error="hasTriedSubmit ? demoErrors[index]?.sourceOther : undefined"
              />

              <div class="col-span-2 flex flex-col gap-1">
                <label :for="`subType-${index}`">Potentieel abonnement</label>
                <SelectButton
                  v-model="demo.subscriptionType"
                  :input-id="`subType-${index}`"
                  :options="subscriptionTypeOptions"
                  option-label="label"
                  option-value="value"
                  class="w-full"
                />
              </div>

              <template v-if="demo.subscriptionType === 'MatchCredits'">
                <div class="col-span-2 flex flex-col gap-1">
                  <label :for="`matchCredits-${index}`">Aantal match credits</label>
                  <InputNumber
                    v-model="demo.matchCredits"
                    :input-id="`matchCredits-${index}`"
                    :min="0"
                    class="w-full"
                  />
                </div>
              </template>

              <template v-if="demo.subscriptionType === 'VacancySlots'">
                <div class="col-span-2 flex flex-col gap-1">
                  <label :for="`subscription-${index}`">Abonnement</label>
                  <SubscriptionInputSelect
                    v-model="demo.subscriptionId"
                    :input-id="`subscription-${index}`"
                  />
                </div>
              </template>
            </div>
          </div>
        </div>
      </template>

      <div class="mt-2 flex justify-end gap-2">
        <Button label="Annuleren" severity="secondary" :disabled="isSaving" @click="closeDialog" />
        <Button
          v-permission="{ resource: AdminPermissionResource.SalesMetrics, level: PermissionLevel.Write }"
          type="submit"
          label="Opslaan"
          :loading="isSaving"
        />
      </div>
    </Form>
  </section>
</template>
